// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SortDirection } from '@/constants/sort';
import {
  DEFAULT_MOVEMENT_PAGE_SIZE,
  MovementHistoryFilter,
  getItemMovementHistory,
} from '@/items/api/get-item-movement-history';
import { useSuspenseQuery } from '@tanstack/react-query';

/**
 * Query key for item movement history.
 */
const itemMovementHistoryQueryKey = (
  itemKey: string,
  filter?: MovementHistoryFilter
) => ['item', itemKey, 'movement', filter] as const;

/**
 * Options for the useItemMovementHistory hook.
 */
interface UseItemMovementHistoryOptions {
  itemKey: string;
  page?: number;
  limit?: number;
  sortOrder?: SortDirection;
}

/**
 * React hook for fetching item movement history.
 * Uses standard useQuery (not suspense) to allow loading states.
 */
export function useItemMovementHistory(options: UseItemMovementHistoryOptions) {
  const {
    itemKey,
    page = 1,
    limit = DEFAULT_MOVEMENT_PAGE_SIZE,
    sortOrder = 'desc',
  } = options;

  const filter: MovementHistoryFilter = { page, limit, sortOrder };

  const { data, isFetching } = useSuspenseQuery({
    queryKey: itemMovementHistoryQueryKey(itemKey, filter),
    queryFn: () => getItemMovementHistory(itemKey, filter),
  });

  return {
    movements: data.results,
    totalCount: data.totalCount,
    isFetching,
  };
}
